import React, { useEffect } from 'react';
import { Redirect, useHistory } from 'react-router-dom';
import {
  CAlert,
  CCol,
  CForm,
  CFormGroup,
  CInput,
  CLabel,
  CRow,
} from '@coreui/react';
import ReCAPTCHA from "react-google-recaptcha";
import { connect, DispatchProp } from "react-redux";
import { useFormik } from 'formik';
import classNames from 'classnames';
import * as Yup from 'yup';
import { some, toPairs } from 'lodash';
import AuthUtils from "../../shared/utils/auth-utils";
import LoadingButton from '../../shared/components/LoadingButton';
import { ResetState, RestorePassword } from '../actions/restore.action';

const invalidUsername = 'Invalid email address';
const errorRequired = 'This field is required';

const validationSchema = Yup.object({
  username: Yup.string().email(invalidUsername).required(errorRequired),
  recaptcha: Yup.string().required(errorRequired),
});

interface RestorePasswordValues {
  username: string;
  recaptcha: string;
}

const initialValues: RestorePasswordValues = {
  username: '',
  recaptcha: ''
};

interface RestorePasswordPageProps extends DispatchProp{
  success: boolean;
  isLoading: boolean;
  errorText: string;
}

const RestorePasswordPage = (props: RestorePasswordPageProps) => {
  const {
    success,
    isLoading,
    errorText,
    dispatch
  } = props;
  const history = useHistory();

  const handleSubmit = (values: RestorePasswordValues) => {
    if (values.username && values.recaptcha) {
      dispatch(new RestorePassword({
        username: values.username,
        captcha: values.recaptcha,
        flow: 'access_restore'
      }))
    }
  };

  useEffect(() => {
    return () => {
      dispatch(new ResetState())
    };
  }, [])


  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  React.useEffect(() => {
    toPairs(initialValues).forEach(([field]) => {
      formik.setFieldTouched(field, false);
    });
  }, [errorText]);

  const getErrorMessage = (error) => {
    switch (error) {
      case 'failed_to_restore':
        return 'Unknown username / email - please update and try again.';
      case 'invalid_captcha_response':
        return 'The CAPTCHA you entered is invalid. Please try again.';
      default:
        return 'Whoops! We had an issue processing your request. Please try again later.';
    }
  };

  const isInvalid = (fieldName: string) => formik.touched[fieldName] && formik.errors[fieldName];

  const isTouched = () => some(formik.touched, Boolean);

  return (
    <>
      { AuthUtils.isAuth() && (<Redirect to="/" />)}
      { (!AuthUtils.isAuth() && !success) ? (
        <>
          <h1 className="text-center">Forgot your password?</h1>
          <CForm onSubmit={formik.handleSubmit}>
            <CFormGroup>
              <CLabel htmlFor="username" className="required">Please enter your username / email to reset your password.</CLabel>
              <CInput
                id="username"
                type="text"
                autoComplete="username"
                name="username"
                className={classNames({'is-invalid': isInvalid('username')})}
                {...formik.getFieldProps('username')}
              />
              {isInvalid('username') && <div className="invalid-feedback">{formik.errors.username}</div>}
            </CFormGroup>
            <CFormGroup>
              <ReCAPTCHA
                sitekey={process.env.CAPTCHA_KEY}
                onChange={(response) => {
                  formik.setFieldValue("recaptcha", response)
                }}
              />
              {isInvalid('recaptcha') && <div className="invalid-feedback d-block">{formik.errors.recaptcha}</div>}
            </CFormGroup>
            <CRow className="mb-2">
              <CCol>
                <LoadingButton
                  type="submit"
                  className="bg-color-orange border-0 pl-4 pr-4 loginPage-loginBtn"
                  isLoading={isLoading}
                >
                  Submit
                </LoadingButton>
              </CCol>
            </CRow>
          </CForm>
          {
            errorText && !isTouched() && (
              <CAlert className="my-10" color="warning" fade>
                  <div>
                    {
                      getErrorMessage(errorText)
                    }
                  </div>
              </CAlert>
            )
          }
        </>
      ) : (
        <>
          <CRow>
            <CCol className="flex-column flex-center">
              <h1 className="text-center">An email has been sent</h1>
              <p className="text-center">Please check your inbox for a link to reset your password.</p>
              <LoadingButton
                className="bg-color-orange border-0"
                onClick={() => history.push('/login')}
                isLoading={false}
              >
                Back to Login
              </LoadingButton>
            </CCol>
          </CRow>
        </>
      )}
    </>
  )
}

const mapStateToProps = (state) => ({
  success: state.restore && state.restore.success,
  isLoading: state.restore && state.restore.isLoading,
  errorText: state.restore && state.restore.errorText
});


export default connect(mapStateToProps)(RestorePasswordPage);
